/** Measures local microphone RMS without recording, sending or playing its audio. */
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

type AudioWindow = Window & {webkitAudioContext?: typeof AudioContext};
interface Meter {
  context: AudioContext;
  stream?: MediaStream;
  source?: MediaStreamAudioSourceNode;
  analyser?: AnalyserNode;
  frame?: number;
}
const audioConstructor = () => typeof window === 'undefined' ? undefined : window.AudioContext ?? (window as AudioWindow).webkitAudioContext;

export function useSIInputLevel({contextKey}: {contextKey: string}) {
  const supported = Boolean(audioConstructor() && typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia);
  const mounted = useRef(false);
  const meter = useRef<Meter | null>(null);
  const [inputLevel, setInputLevel] = useState(0);
  const stop = useCallback(() => {
    const active = meter.current;
    meter.current = null;
    if (active) {
      if (active.frame !== undefined) cancelAnimationFrame(active.frame);
      active.source?.disconnect();
      active.analyser?.disconnect();
      active.stream?.getTracks().forEach(track => track.stop());
      void active.context.close().catch(() => { /* Closing an already retired browser context can reject. */ });
    }
    if (mounted.current) setInputLevel(0);
  }, []);
  useLayoutEffect(() => {
    mounted.current = true;
    stop();
    return () => {mounted.current = false; stop();};
  }, [contextKey, stop]);
  useEffect(() => {
    const hide = () => {if (document.visibilityState === 'hidden') stop();};
    window.addEventListener('pagehide', stop);
    document.addEventListener('visibilitychange', hide);
    return () => {window.removeEventListener('pagehide', stop); document.removeEventListener('visibilitychange', hide);};
  }, [stop]);
  const start = useCallback(() => {
    stop();
    const Audio = audioConstructor();
    if (!mounted.current || !Audio || !navigator.mediaDevices?.getUserMedia) return;
    let active: Meter;
    try {
      // Construct and resume in the explicit button gesture, before awaiting permission.
      active = {context: new Audio()};
    } catch {return;}
    meter.current = active;
    const current = () => mounted.current && meter.current === active;
    const fail = () => {if (current()) stop();};
    try {
      void active.context.resume().catch(fail);
      void navigator.mediaDevices.getUserMedia({audio: true, video: false}).then(stream => {
        if (!current()) {stream.getTracks().forEach(track => track.stop()); return;}
        active.stream = stream;
        try {
          active.source = active.context.createMediaStreamSource(stream);
          active.analyser = active.context.createAnalyser();
          active.analyser.fftSize = 512;
          active.source.connect(active.analyser);
          // No destination connection: the meter cannot feed the mic back through speakers.
          const samples = new Float32Array(active.analyser.fftSize);
          let lastPublished = -Infinity;
          const sample = (time: number) => {
            if (!current()) return;
            if (time - lastPublished >= 100) {
              try {
                active.analyser!.getFloatTimeDomainData(samples);
                let squares = 0;
                for (const value of samples) squares += value * value;
                const level = Math.min(1, Math.sqrt(squares / samples.length));
                lastPublished = time;
                setInputLevel(Number.isFinite(level) ? level : 0);
              } catch {fail(); return;}
            }
            active.frame = requestAnimationFrame(sample);
          };
          active.frame = requestAnimationFrame(sample);
        } catch {fail();}
      }).catch(fail);
    } catch {fail();}
  }, [stop]);
  return {inputLevel, supported, start, stop};
}
